<script>
  import { onMount, onDestroy } from 'svelte';
  import Button from '../components/Button.svelte';
  import { heroBackgrounds, heroBenefits, heroInfo } from './hero.js';

  const desktopLines = [
    `${heroInfo.subtitle[0]} ${heroInfo.subtitle[1]}`,
    heroInfo.subtitle[2]
  ];
  const stackedLines = heroInfo.subtitle;
  const totalChars = desktopLines.reduce((sum, l) => sum + l.length, 0);

  let typed = 0;
  let done = false;
  let intervalId;

  let heroSection;
  let parallaxEnabled = false;
  let parallaxX = 0;
  let parallaxY = 0;
  let rafId = 0;
  let targetX = 0;
  let targetY = 0;

  function handlePointerMove(event) {
    if (!parallaxEnabled || !heroSection) return;
    const rect = heroSection.getBoundingClientRect();
    targetX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    targetY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    if (!rafId) rafId = requestAnimationFrame(updateParallax);
  }

  function handlePointerLeave() {
    targetX = 0;
    targetY = 0;
    if (!rafId) rafId = requestAnimationFrame(updateParallax);
  }

  function updateParallax() {
    rafId = 0;
    const dx = targetX - parallaxX;
    const dy = targetY - parallaxY;
    parallaxX += dx * 0.12;
    parallaxY += dy * 0.12;
    if (Math.abs(dx) > 0.001 || Math.abs(dy) > 0.001) {
      rafId = requestAnimationFrame(updateParallax);
    } else {
      parallaxX = targetX;
      parallaxY = targetY;
    }
  }

  function sliceLines(arr, count) {
    const result = [];
    let remaining = count;
    for (const line of arr) {
      if (remaining <= 0) {
        result.push('');
      } else if (remaining < line.length) {
        result.push(line.slice(0, remaining));
        remaining = 0;
      } else {
        result.push(line);
        remaining -= line.length;
      }
    }
    return result;
  }

  $: desktopTyped = sliceLines(desktopLines, typed);
  $: stackedTyped = sliceLines(stackedLines, typed);
  $: desktopCursorIdx = (() => {
    let acc = 0;
    for (let i = 0; i < desktopLines.length; i++) {
      acc += desktopLines[i].length;
      if (typed <= acc) return i;
    }
    return desktopLines.length - 1;
  })();
  $: stackedCursorIdx = (() => {
    let acc = 0;
    for (let i = 0; i < stackedLines.length; i++) {
      acc += stackedLines[i].length;
      if (typed <= acc) return i;
    }
    return stackedLines.length - 1;
  })();

  onMount(() => {
    const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1280px)').matches;
    parallaxEnabled = finePointer && !reducedMotion;

    if (reducedMotion) {
      typed = totalChars;
      done = true;
      return;
    }
    intervalId = setInterval(() => {
      typed += 1;
      if (typed >= totalChars) {
        clearInterval(intervalId);
        done = true;
      }
    }, 55);
  });

  onDestroy(() => {
    if (intervalId) clearInterval(intervalId);
    if (rafId) cancelAnimationFrame(rafId);
  });
</script>

<section
  class="hero-section"
  class:parallax-active={parallaxEnabled}
  aria-labelledby="hero-title"
  bind:this={heroSection}
  on:mousemove={handlePointerMove}
  on:mouseleave={handlePointerLeave}
  style="--parallax-x: {parallaxX}; --parallax-y: {parallaxY};"
>
  <picture class="hero-media" aria-hidden="true">
    <source media="(max-width: 767px)" srcset={heroBackgrounds.mobile}>
    <source media="(max-width: 1279px)" srcset={heroBackgrounds.tablet}>
    <img src={heroBackgrounds.desktop} alt="">
  </picture>
  <div class="hero-overlay" aria-hidden="true"></div>

  <div class="hero-frame">
    <div class="hero-content">
      <p class="hero-eyebrow hero-fade hero-fade-1">
        <span class="hero-date"><strong>{heroInfo.eyebrow.day}</strong> {heroInfo.eyebrow.month} <strong>{heroInfo.eyebrow.year}</strong>,</span>
        <span class="hero-place">{heroInfo.eyebrow.place}</span>
      </p>

      <div class="hero-copy hero-fade hero-fade-2">
        <h1 id="hero-title">{#if heroInfo.titlePrefix}{heroInfo.titlePrefix} {/if}<span>{heroInfo.titleAccent}</span> {heroInfo.title}</h1>

        <div class="hero-subtitle hero-subtitle-desktop" aria-label={desktopLines.join(' ')}>
          {#each desktopTyped as line, i}
            <p>{line}{#if !done && desktopCursorIdx === i}<span class="type-cursor" aria-hidden="true"></span>{/if}</p>
          {/each}
        </div>

        <div class="hero-subtitle hero-subtitle-stacked" aria-label={stackedLines.join(' ')}>
          {#each stackedTyped as line, i}
            <p>{line}{#if !done && stackedCursorIdx === i}<span class="type-cursor" aria-hidden="true"></span>{/if}</p>
          {/each}
        </div>

        <div class="hero-line"></div>
      </div>

      <div class="benefits hero-fade hero-fade-3" aria-label="Преимущества форума">
        {#each heroBenefits as benefit, index}
          <div class="benefit-item">
            <img class="benefit-icon" src={benefit.iconSrc} alt="" aria-hidden="true">
            <span class="benefit-label">
              {#each benefit.label as line}
                <span>{line}</span>
              {/each}
            </span>
          </div>
          {#if index < heroBenefits.length - 1}
            <span class="benefit-separator" aria-hidden="true"></span>
          {/if}
        {/each}
      </div>

      <div class="hero-actions hero-fade hero-fade-4">
        <Button
          href={heroInfo.primaryAction.href}
          label={heroInfo.primaryAction.label}
          target={heroInfo.primaryAction.target}
          rel={heroInfo.primaryAction.rel}
          variant="primary"
        />
        <Button href={heroInfo.secondaryAction.href} label={heroInfo.secondaryAction.label} variant="secondary" />
      </div>
    </div>
  </div>
</section>

<style>
  .hero-section {
    position: relative;
    display: flex;
    min-height: 880px;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
    background: #000000;
    color: #ffffff;
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-variation-settings: 'wdth' 100;
    box-sizing: border-box;
    --parallax-x: 0;
    --parallax-y: 0;
  }

  .hero-media,
  .hero-media img,
  .hero-overlay {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .hero-media {
    will-change: transform;
    transition: transform 600ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .hero-media img {
    object-fit: contain;
    object-position: center right;
  }

  .parallax-active .hero-media {
    transform: scale(1.05) translate3d(calc(var(--parallax-x) * -18px), calc(var(--parallax-y) * -14px), 0);
    transition: none;
  }

  .parallax-active .hero-content {
    transform: translate3d(calc(var(--parallax-x) * 8px), calc(var(--parallax-y) * 6px), 0);
    transition: none;
  }

  .hero-overlay {
    z-index: 1;
    background: rgba(0, 0, 0, 0.2);
    pointer-events: none;
  }

  .hero-frame {
    position: relative;
    z-index: 2;
    display: flex;
    width: min(100%, 1440px);
    min-height: 800px;
    align-items: center;
    margin: 0 auto;
    padding: 180px 64px 80px;
    box-sizing: border-box;
  }

  .hero-content {
    display: flex;
    width: 100%;
    max-width: 920px;
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    padding: 4px 12px 0;
    box-sizing: border-box;
    will-change: transform;
    transition: transform 600ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .hero-eyebrow,
  .hero-subtitle p {
    margin: 0;
  }

  .hero-eyebrow {
    display: flex;
    flex-wrap: wrap;
    gap: 0 5px;
    color: #ffffff;
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: normal;
    text-transform: uppercase;
  }

  .hero-eyebrow strong {
    color: #e7b962;
    font-weight: 500;
  }

  .hero-copy {
    display: flex;
    flex-direction: column;
    gap: 29px;
    align-items: flex-start;
  }

  h1 {
    margin: 0;
    color: #ffffff;
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-size: 94px;
    font-weight: 700;
    line-height: normal;
    text-transform: uppercase;
    white-space: nowrap;
  }

  h1 span {
    color: #e7b962;
  }

  .hero-subtitle {
    color: #ffffff;
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-size: 40px;
    font-weight: 300;
    line-height: normal;
    letter-spacing: 1px;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .hero-subtitle-stacked {
    display: none;
  }

  .hero-subtitle p {
    min-height: 1em;
  }

  .hero-fade {
    opacity: 0;
    transform: translateY(16px);
    animation: hero-fade-in 700ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  }

  .hero-fade-1 { animation-delay: 100ms; }
  .hero-fade-2 { animation-delay: 250ms; }
  .hero-fade-3 { animation-delay: 600ms; }
  .hero-fade-4 { animation-delay: 800ms; }

  @keyframes hero-fade-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .hero-fade {
      opacity: 1;
      transform: none;
      animation: none;
    }
  }

  .type-cursor {
    display: inline-block;
    width: 0.55ch;
    height: 0.85em;
    margin-left: 4px;
    background: #e7b962;
    vertical-align: -0.05em;
    animation: type-blink 850ms steps(2, start) infinite;
  }

  @keyframes type-blink {
    to { visibility: hidden; }
  }

  @media (prefers-reduced-motion: reduce) {
    .type-cursor { display: none; }
  }

  .hero-line {
    width: 119px;
    height: 4px;
    background: #ffffff;
  }

  .benefits {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 24px 0;
  }

  .benefit-item {
    display: flex;
    min-width: 60px;
    flex-direction: column;
    align-items: flex-start;
    gap: 11px;
  }

  .benefit-icon {
    display: block;
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  .benefit-label {
    display: flex;
    flex-direction: column;
    color: #ffffff;
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.5;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .benefit-separator {
    width: 1px;
    height: 40px;
    background: rgba(255, 255, 255, 0.25);
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 21px;
  }

  @media (max-width: 1279px) {
    .hero-section {
      min-height: 860px;
      padding-top: 40px;
    }

    .hero-media img {
      object-fit: cover;
      object-position: center right;
    }

    .hero-frame {
      min-height: 760px;
      padding: 160px 32px 80px;
    }

    .hero-content {
      gap: 24px;
      padding: 0 12px;
    }

    .hero-eyebrow {
      font-size: 16px;
    }

    .hero-copy {
      gap: 16px;
    }

    h1 {
      font-size: 52px;
      line-height: 1.1;
    }

    .hero-subtitle {
      font-size: 26px;
      line-height: normal;
      letter-spacing: 0.65px;
    }

    .hero-subtitle-desktop {
      display: none;
    }

    .hero-subtitle-stacked {
      display: block;
    }
  }

  @media (max-width: 1599px) and (min-width: 1280px) {
    h1 {
      font-size: 64px;
      line-height: 1.1;
    }

    .hero-subtitle {
      font-size: 30px;
      letter-spacing: 0.75px;
    }
  }

  @media (max-width: 767px) {
    .hero-section {
      min-height: 472px;
      padding-top: 0;
    }

    .hero-media img {
      object-fit: cover;
      object-position: 70% center;
    }

    .hero-frame {
      min-height: 472px;
      padding: 100px 24px 40px;
    }

    .hero-content {
      gap: 24px;
      padding: 0;
    }

    .hero-eyebrow {
      max-width: 300px;
      flex-direction: column;
      gap: 0;
      font-size: 14px;
      line-height: normal;
    }

    h1 {
      font-size: 32px;
      line-height: normal;
      white-space: normal;
    }

    .hero-subtitle {
      font-size: 18px;
      line-height: normal;
      letter-spacing: 0.45px;
      white-space: normal;
    }

    .benefits {
      display: none;
    }

    .hero-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
      gap: 21px;
    }

    .hero-actions :global(.button) {
      width: 100%;
    }
  }
</style>
